import { z } from 'zod';

// Snake_case to match trendz-db's master.crop seed convention
// (e.g. 'cotton', 'dry_chilli'). Different from tag codes (kebab-case).
// Server enforces the same regex on CreateCropDto; mirrored here for
// inline UX feedback.
const CROP_CODE_REGEX = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;

// ────────────────────────────────────────────────────────────────────────────
// Server-shaped row (validated when consumed)
// ────────────────────────────────────────────────────────────────────────────

export const cropSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  category: z.string().nullable(),
  imageStorageKey: z.string().nullable(),
  thumbnailStorageKey: z.string().nullable(),
  displayOrder: z.number().int().nullable(),
  isPreselected: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Crop = z.infer<typeof cropSchema>;

const cropTranslationSchema = z.object({
  id: z.string(),
  localeId: z.string(),
  localeCode: z.string(),
  name: z.string(),
});
export const cropDetailSchema = cropSchema.extend({
  translations: z.array(cropTranslationSchema),
});
export type CropDetail = z.infer<typeof cropDetailSchema>;

export const cropOptionSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
});
export type CropOption = z.infer<typeof cropOptionSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Form schema
// ────────────────────────────────────────────────────────────────────────────

export const cropFormTranslationSchema = z.object({
  localeCode: z.string().min(1),
  localeName: z.string(),
  name: z.string().max(200),
});
export type CropFormTranslation = z.infer<typeof cropFormTranslationSchema>;

export const cropFormSchema = z.object({
  code: z
    .string()
    .min(1, 'Code is required')
    .max(100)
    .regex(
      CROP_CODE_REGEX,
      'Lowercase alphanumerics joined by single underscores',
    ),
  name: z.string().min(1, 'Name is required').max(200),
  category: z.string().max(100).nullable(),
  imageStorageKey: z.string().max(500).nullable(),
  thumbnailStorageKey: z.string().max(500).nullable(),
  displayOrder: z.number().int().nullable(),
  isPreselected: z.boolean(),
  isActive: z.boolean(),
  translations: z.array(cropFormTranslationSchema),
});
export type CropFormValues = z.infer<typeof cropFormSchema>;

// ────────────────────────────────────────────────────────────────────────────
// List filters
// ────────────────────────────────────────────────────────────────────────────

export const CROP_SORT_KEYS = [
  'code',
  'name',
  'displayOrder',
  'created',
  'updated',
] as const;
export type CropSortKey = (typeof CROP_SORT_KEYS)[number];

export type SortDirection = 'asc' | 'desc';

export interface ListCropsParams {
  page: number;
  pageSize: number;
  searchText?: string;
  isActive?: 'true' | 'false';
  sortBy?: CropSortKey;
  sortDir?: SortDirection;
}
